import { observer } from "mobx-react-lite";
import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useStore } from "../../helpers";
import _ from "lodash";
import CreatableSelect from "react-select/creatable";
import { FTPBreadCrumbs } from "../../components-new/BreadCrumbs";
import { htmlValidation, makeid_alpa_numeric } from "../../helpers/utils";
import { Checkbox } from "@material-ui/core";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components/DeleteModal";
import { PRINT_MEDIA_LANGUAGES } from "./print-media-settings";

const crumbs = [
    { name: "Media Dashboard", href: "/media", current: false },
    { name: "Print Media Reports", href: "/print_media_reports", current: false },
    { name: "Print Media Reports Edit", href: "#", current: false },
];

export const PrintMediaReportsEdit = observer((props: any) => {
    const rootStore = useStore();
    const { reportid } = useParams<{ reportid: string }>();
    const { userStore, reportStore, mediaStore } = rootStore;

    const [report, setReport] = useState({});
    const [allTags, setAllTags] = useState([]);

    const [error, setError] = useState(false);

    const [fetched, setFetched] = useState(false);
    const [loading, setLoading] = useState(false);

    const [activeGroup, setActiveGroup] = React.useState("");
    const [groupEditMode, setGroupEditMode] = React.useState(false);
    const [groupSearchTermEditMode, setGroupSearchTermEditMode] = React.useState(false);

    useEffect(() => {
        fetchReport();
        fetchAllTags();
    }, []);

    const fetchReport = async () => {
        setLoading(true);
        const response = await mediaStore.viewPrintMediaReport(parseInt(reportid ?? "0", 10));
        if (response.status !== 200) {
            setError(true);
            setLoading(false);
            return;
        }
        setReport(response.data);
        setFetched(true);
        setLoading(false);
    };

    const fetchAllTags = async () => {
        const response = await reportStore.fetchAllTags();
        if (response.status !== 200) {
            setError(true);
            setLoading(false);
            return;
        }
        setAllTags(response.data);
    };

    const CtrlS_detector = async (event) => {
        if (event.ctrlKey === true && event.keyCode === 83) {
            htmlValidation(event);
            await saveReport();
        }
    };

    const Loading = () => (
        <div className="ml-4 text-gray-500">
            <i className="fa-2x fa-solid fa-spinner fa-pulse" />
        </div>
    );

    const saveReport = async () => {
        if (loading || !fetched) return;
        setLoading(true);
        const response = await mediaStore.updatePrintMediaReport(report);
        if (response.status !== 200) {
            setError(true);
            setLoading(false);
            return;
        }
        setError(false);
        setReport(response.data);
        setFetched(true);
        setLoading(false);
    };

    const Actions = (innerprops) => {
        return (
            <div className="ml-auto">
                <button className="rounded-md bg-indigo-700 px-4 py-2 text-white" onClick={saveReport}>
                    Save
                </button>
            </div>
        );
    };

    const Error = (innerprops) => {
        return (
            <div className="italic text-red-600">
                Error occured while completing operation. Please try again or contact support team.
            </div>
        );
    };

    const languages = PRINT_MEDIA_LANGUAGES;

    const defaultSearchTerm = {
        query: "",
        lang: "",
    };
    const [st, setSt] = useState(defaultSearchTerm);

    const SearchGroups = () => (
        <div className="text-md mt-5 flex flex-row items-center text-gray-700">
            <div className="font-bold uppercase text-gray-700">
                Search Groups
                {userStore.isRoot() ? (
                    <i
                        className="fa-solid fa-plus-circle cursor-pointer pl-3"
                        title="Add New Search Term"
                        onClick={() => {
                            const temp = _.clone(report);
                            const gp = getEmptySearchGroup();
                            temp["queries"][gp.id] = gp;
                            setReport(temp);
                            toast.success("New Group added successfully");
                        }}
                    />
                ) : null}
            </div>
        </div>
    );

    const AddNewSearchTerm = () => (
        <div className="mt-5 grid grid-cols-5 items-center gap-2 lg:pb-4">
            <div className="flex flex-col">
                <label htmlFor="query" className="block text-sm font-medium text-gray-700">
                    Search Term
                </label>
                <input
                    className="h-10 rounded-lg border-2 border-gray-300 bg-white px-3 text-sm focus:outline-none"
                    value={st.query}
                    name="query"
                    type="text"
                    onChange={(evt) => setSt({ ...st, query: evt.target.value })}
                />
            </div>
            <div className="flex flex-col">
                <label htmlFor="lang" className="block text-sm font-medium text-gray-700">
                    Language
                </label>
                <select
                    className="h-10 rounded-lg border-2 border-gray-300 bg-white px-3 text-sm focus:outline-none"
                    value={st.lang}
                    onChange={(evt) => setSt({ ...st, lang: evt.target.value })}
                >
                    <option value="">---Select---</option>
                    {Object.keys(languages).map((key) => (
                        <option key={key} value={key}>
                            {languages[key]}
                        </option>
                    ))}
                </select>
            </div>
            <div className="flex flex-row pt-6">
                <button
                    type="button"
                    className="rounded-md bg-indigo-700 px-4 py-2 text-sm font-medium text-white lg:mr-2"
                    onClick={() => {
                        const temp = _.clone(report);
                        temp["queries"][activeGroup]["terms"].push(st);
                        setReport(temp);
                        setSt(_.clone(defaultSearchTerm));
                        setGroupSearchTermEditMode(false);
                    }}
                >
                    Add
                </button>
                <button
                    type="button"
                    className="rounded-md bg-red-700 px-4 py-2 text-sm font-medium text-white"
                    onClick={() => setGroupSearchTermEditMode(false)}
                >
                    Cancel
                </button>
            </div>
        </div>
    );

    const getEmptySearchGroup = () => {
        const genid = makeid_alpa_numeric(6);
        return {
            id: genid,
            name: genid,
            terms: [],
            mentions: true,
            articles: true,
            interest: true,
            comparison: false,
        };
    };

    const deleteGroupCB = (_e, groupId) => {
        const temp = _.clone(report);
        delete temp["queries"][groupId];
        setReport(temp);
        setDeleteGroupModal(false);
        toast.success("Group deleted successfully !");
    };

    const cancelCB = () => {
        setDeleteGroupModal(false);
    };

    const [deleteGroupModal, setDeleteGroupModal] = useState(false);
    const RenderSearchGroup = (gp) => {
        const { id, name, terms, mentions, articles, interest, comparison } = gp;

        // @ts-ignore
        const viewMode = (
            <>
                <div className="flex flex-row items-center">
                    <div className="text-lg font-bold">{name}</div>

                    <div
                        className="mx-2 cursor-pointer text-blue-600"
                        title="Edit group name"
                        onClick={() => {
                            setActiveGroup(id);
                            setGroupEditMode(true);
                        }}
                    >
                        <i className="fa-solid fa-edit" />
                    </div>

                    <div
                        className="mx-2 cursor-pointer text-red-500"
                        title="Delete group"
                        onClick={() => {
                            setDeleteGroupModal(true);
                            setActiveGroup(id);
                        }}
                    >
                        <i className="fa-solid fa-trash" />
                    </div>

                    <div
                        className="mx-2 cursor-pointer text-green-500"
                        title="Add Search Terms"
                        onClick={() => {
                            setActiveGroup(id);
                            setGroupSearchTermEditMode(true);
                        }}
                    >
                        <i className="fa-solid fa-plus" />
                    </div>
                    {deleteGroupModal ? (
                        <DeleteModal
                            onDeleteClick={deleteGroupCB}
                            onCancelClick={cancelCB}
                            isOpen={deleteGroupModal}
                            id={id}
                            desc="Do you want to delete the group"
                        />
                    ) : null}
                </div>
            </>
        );

        const editMode = (
            <>
                <div className="flex flex-row items-center">
                    <input
                        type="text"
                        value={name}
                        onChange={(event) => {
                            const changed = event.target.value;
                            const temp = _.clone(report);
                            temp["queries"][id]["name"] = changed;
                            setReport(temp);
                        }}
                        className="h-8 rounded-md rounded-none rounded-r-md border-gray-300 px-2 py-2 shadow-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        placeholder="Group name..."
                    />
                    <div
                        className="mx-2 cursor-pointer text-xl text-blue-600"
                        onClick={() => {
                            setGroupEditMode(false);
                        }}
                    >
                        <i className="fa-solid fa-save"></i>
                    </div>
                </div>
            </>
        );

        return (
            <>
                <div className="my-2 rounded-lg border-2 border-gray-400 p-2">
                    <div className="my-2 flex flex-row flex-wrap md:ml-2 lg:ml-4">
                        {activeGroup && groupEditMode && activeGroup === id ? editMode : viewMode}
                    </div>

                    <div className="md:ml-6 lg:ml-10">
                        <div className="font-bold text-gray-500">Configuration</div>
                        <div className="flex flex-col">
                            <div className="flex flex-row items-center">
                                <Checkbox
                                    checked={mentions}
                                    onChange={(evt) => {
                                        const changed = evt.target.checked;
                                        const temp = _.clone(report);
                                        temp["queries"][id]["mentions"] = changed;
                                        setReport(temp);
                                    }}
                                    size="small"
                                    inputProps={{ "aria-label": "checkbox with small size" }}
                                />
                                <div>Include for computing Mentions</div>
                            </div>

                            <div className="flex flex-row items-center">
                                <Checkbox
                                    checked={articles}
                                    onChange={(evt) => {
                                        const changed = evt.target.checked;
                                        const temp = _.clone(report);
                                        temp["queries"][id]["articles"] = changed;
                                        setReport(temp);
                                    }}
                                    size="small"
                                    inputProps={{ "aria-label": "checkbox with small size" }}
                                />
                                <div>Include for computing Articles</div>
                            </div>

                            <div className="flex flex-row items-center">
                                <Checkbox
                                    checked={comparison}
                                    onChange={(evt) => {
                                        const changed = evt.target.checked;
                                        const temp = _.clone(report);
                                        temp["queries"][id]["comparison"] = changed;
                                        setReport(temp);
                                    }}
                                    size="small"
                                    inputProps={{ "aria-label": "checkbox with small size" }}
                                />
                                <div>Include for Comparisons</div>
                            </div>

                            <div className="flex flex-row items-center">
                                <Checkbox
                                    checked={interest}
                                    onChange={(evt) => {
                                        const changed = evt.target.checked;
                                        const temp = _.clone(report);
                                        temp["queries"][id]["interest"] = changed;
                                        setReport(temp);
                                    }}
                                    size="small"
                                    inputProps={{ "aria-label": "checkbox with small size" }}
                                />
                                <div>Track as interest</div>
                            </div>
                        </div>

                        <div className="font-bold text-gray-500">Search Terms</div>
                        {activeGroup && groupSearchTermEditMode && activeGroup === id ? <AddNewSearchTerm /> : null}

                        <div className="grid grid-cols-1 gap-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6">
                            {terms.map((s, index) => {
                                return (
                                    <>
                                        <div className="flex flex-row justify-between rounded-md bg-white p-2 shadow-md">
                                            <div className="flex flex-col">
                                                <div
                                                    className="text-md text-lg font-bold text-indigo-600"
                                                    key={`group_${id}_ind_${index}`}
                                                >
                                                    {s.query}
                                                </div>
                                                <div className="text-sm text-gray-500">
                                                    {languages[s.lang]}
                                                </div>
                                            </div>
                                            <div
                                                className="font-2xl cursor-pointer text-red-500"
                                                title="Delete searh term"
                                                onClick={() => {
                                                    const temp = _.clone(report);
                                                    temp["queries"][id]["terms"].splice(index, 1);
                                                    setReport(temp);
                                                }}
                                            >
                                                <i className="fa-solid fa-trash" />
                                            </div>
                                        </div>
                                    </>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </>
        );
    };

    const ShowSearchGroups = () => {
        return (
            <>
                {Object.keys(report["queries"]).map((key) => {
                    const gp = report["queries"][key];
                    return <>{RenderSearchGroup(gp)}</>;
                })}
            </>
        );
    };

    const PropertiesTab = () => {
        return (
            <>
                <div className="mt-5">
                    <label className="block text-sm font-bold font-medium uppercase text-gray-700">
                        Report Name
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                        <input
                            type="text"
                            value={report["name"]}
                            onChange={(event) => {
                                setReport({ ...report, name: event.target.value });
                            }}
                            className="block h-8 w-full flex-1 rounded-md rounded-none rounded-r-md border-gray-300 px-2 py-2 shadow-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Report name..."
                        />
                    </div>
                </div>

                <div className="mt-5">
                    <label className="block text-sm font-bold font-medium uppercase text-gray-700">
                        Report Description
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                        <textarea
                            rows={5}
                            value={report["description"]}
                            onChange={(event) => {
                                setReport({ ...report, description: event.target.value });
                            }}
                            className="block w-full flex-1 rounded-md rounded-none rounded-r-md border-gray-300 px-2 py-2 shadow-md focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                            placeholder="Report name..."
                        />
                    </div>
                </div>

                <div className="mt-5">
                    <label className="block text-sm font-bold font-medium uppercase text-gray-700">
                        Tags
                    </label>
                    <div className="mt-1 flex rounded-md shadow-sm">
                        <CreatableSelect
                            isClearable
                            isMulti
                            onChange={(val) => {
                                const updatedTags = (val ?? []).map((x) => x.value);
                                setReport({ ...report, tags: updatedTags });
                            }}
                            value={report["tags"].map((x) => {
                                return { value: x, label: x };
                            })}
                            options={allTags.map((x) => {
                                return { value: x, label: x };
                            })}
                            placeholder="Select tags..."
                            className="basic-multi-select z-0 h-10 w-full rounded-md border-gray-300 shadow-md outline-none focus:shadow"
                            classNamePrefix="select"
                        />
                    </div>
                </div>
                <SearchGroups />
                <ShowSearchGroups />
            </>
        );
    };

    return (
        <>
            <FTPBreadCrumbs crumbs={crumbs} />
            <div
                className="p-4"
                onKeyDownCapture={CtrlS_detector}
                tabIndex={0}
                style={{ minHeight: "calc(100vh - 45px)" }}
            >
                <div className="flex flex-row flex-nowrap items-center">
                    {fetched ? (
                        <div className="text-3xl font-bold text-gray-800">{report["name"]}</div>
                    ) : (
                        <div className="text-3xl font-bold text-gray-800">Edit Report</div>
                    )}
                    {loading ? <Loading /> : null}
                    {<Actions />}
                </div>

                {error ? <Error /> : null}
                {fetched ? PropertiesTab() : null}
            </div>
        </>
    );
});
